import React, { FC, useEffect, useState } from 'react'

import { Skeleton } from '@mui/material'
import clsx from 'clsx'
import Select from 'react-select'
import { where } from 'firebase/firestore'

import InfiniteScrollDataTable from '../../../../../../../components/common/tables/InfiniteScrollDataTable'
import ViewsLoader from '../../../../../../../components/loader/ViewsLoader'
import { ScoreCardOption } from '../../../components/ScoreCardOption'

import FirestoreService from '../../../../../../../services/firestoreService'

import { useAppSelector } from '../../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../../store/user/userSlice'

import { ScoreCardModel } from '../../../../../../../models/score-cards/score-cards.model'

import {
  IEventFees,
  IJudgeOption,
} from '../../../../../../../models/event-fees/event-fees.interface'
import {
  IUpdateRegistrationByDayName,
  RegistrationFeesTypeEx,
} from '../../ManageClinicNOtherOperationsTab'
import { IScoreCardInterface } from '../../../../../../../models/score-cards/score-cards.interface'

import { dummySheets } from '../../../data/sheets'

import { CONST } from '../../../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

const scoreCardOptions: IJudgeOption[] = [
  { label: 'Default', value: 'Default', disable: false },
  ...dummySheets.map((sheet) => ({
    label: sheet.name,
    value: sheet.id,
    disable: false,
  })),
]

const initialScoreCardHunterJumperOptions: IJudgeOption[] = [
  { label: 'Equitation', value: 'Equitation', disable: false },
  { label: 'Hunters', value: 'Hunters', disable: false },
  {
    label: 'Add custom score card',
    value: 'Add custom score card',
    disable: false,
    isBorderTop: true,
    onAddScoreCard: true,
  },
]

const disciplineOptions: IJudgeOption[] = [
  { label: 'Universal', value: 'Universal', disable: false },
  { label: 'Dressage', value: 'Dressage', disable: false },
  { label: 'Eventing', value: 'Eventing', disable: true },
  { label: 'Hunter Jumper', value: 'Hunter Jumper', disable: false },
  { label: 'AQHA', value: 'AQHA', disable: true },
  { label: 'Reining', value: 'Reining', disable: true },
]

interface JudgingTableProps {
  onChangeJudge: (
    row: RegistrationFeesTypeEx,
    values: { label: string; value: string }[]
  ) => Promise<void>
  updateRegistrationByDayName: ({
    feeName,
    sheetId,
    discipline,
    values,
  }: IUpdateRegistrationByDayName) => Promise<void>
  updateFeesKeys: (keys: object, uuid: string, parentId?: string) => Promise<void>
  registrationFees: IEventFees | null
  judgesOptions: IJudgeOption[] | null
}

export const JudgingTable: FC<JudgingTableProps> = ({
  onChangeJudge,
  updateRegistrationByDayName,
  updateFeesKeys,
  registrationFees,
  judgesOptions,
}) => {
  const profileData = useAppSelector(selectProfileData)

  const [currentLoading, currentSetLoading] = useState(false)
  const [scoreCarLoading, setScoreCarLoading] = useState(false)
  const [scoreCardsByUser, setScoreCardsByUser] = useState<IScoreCardInterface[] | null>(null)
  const [scoreCardHunterJumperOptions, setScoreCardHunterJumperOptions] = useState<IJudgeOption[]>(
    initialScoreCardHunterJumperOptions
  )

  const getScoreCardsByUser = async () => {
    const scoreCards: IScoreCardInterface[] = []

    setScoreCarLoading(true)
    try {
      const scoreCardsSnaps = await FirestoreService.filterItems(COLLECTIONS.SCORE_CARDS.NAME, [
        where(COLLECTIONS.SCORE_CARDS.FIELDS.OWNER.KEY, '==', profileData.id),
      ])

      scoreCardsSnaps.forEach((doc) => {
        const item = ScoreCardModel.fromFirestoreDoc(doc).toObject()
        scoreCards.push(item)
      })
      setScoreCardsByUser(scoreCards)
    } catch (error) {
      console.error(error)
    } finally {
      setScoreCarLoading(false)
    }
  }

  useEffect(() => {
    getScoreCardsByUser().then()
  }, [profileData.id])

  useEffect(() => {
    setScoreCardHunterJumperOptions([
      ...initialScoreCardHunterJumperOptions,
      ...(scoreCardsByUser
        ? scoreCardsByUser
            .filter((scoreCard) => scoreCard.type === 'hunter-jumper')
            .map((scoreCard) => ({
              label: scoreCard.name,
              value: scoreCard.id ?? '',
              disable: false,
              isCustom: true,
            }))
        : []),
    ])
  }, [scoreCardsByUser])

  const onChangeSelect = async (
    row: RegistrationFeesTypeEx,
    value: string,
    updated: { [key: string]: string | null }
  ) => {
    await updateFeesKeys(updated, row.uuid, row.parentId)

    // we need rewrite old score cards
    await updateRegistrationByDayName({
      feeName: row.name ?? '',
      sheetId: value,
      discipline: row.discipline ?? null,
    })
  }

  const isFilled = (row: RegistrationFeesTypeEx) => ({
    borderColor: '#D3DAEE',
    backgroundColor:
      row.judges && row.judges.length > 0 && row.scoreCard && row.discipline ? '#F6F7FB' : '#fff',
  })

  const getValues = (field: string, options: IJudgeOption[]) => {
    return field
      ? {
          value: options.find((sheet) => sheet.value === field)?.value ?? '',
          label: options.find((sheet) => sheet.value === field)?.label ?? '',
          disable: options.find((sheet) => sheet.value === field)?.disable ?? false,
        }
      : null
  }

  const columnsJudging = [
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Class</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div
            className={clsx(
              'my-2 p-2 min-h-[56px] w-full rounded-md flex items-center border border-SeabiscuitLightThemeColorD3',
              row.judges && row.judges.length > 0 && row.scoreCard && 'bg-SeabiscuitGrayThemeColor'
            )}
          >
            {row.name}
          </div>
        ),
      width: '28%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Discipline</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={true}
              placeholder="Select Discipline"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  color: state.isDisabled ? '#122B4680' : baseStyles.color,
                }),
              }}
              value={getValues(row.discipline ?? '', disciplineOptions)}
              options={disciplineOptions}
              isOptionDisabled={(option: IJudgeOption) => option.disable}
              onChange={async (value) => {
                currentSetLoading(true)
                if (value)
                  await onChangeSelect(row, value.value, {
                    discipline: value.value,
                    scoreCard: null,
                    judges: null,
                  })
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: '16%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Score cards</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="98%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={true}
              placeholder="Select score card"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              isLoading={scoreCarLoading}
              onMenuOpen={getScoreCardsByUser}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
              }}
              value={getValues(row.scoreCard ?? '', [
                ...scoreCardOptions,
                ...scoreCardHunterJumperOptions,
              ])}
              options={
                row.discipline === 'Universal'
                  ? [...scoreCardOptions, ...scoreCardHunterJumperOptions]
                  : row.discipline === 'Dressage'
                    ? scoreCardOptions
                    : row.discipline === 'Hunter Jumper'
                      ? scoreCardHunterJumperOptions
                      : []
              }
              components={{ Option: (props) => <ScoreCardOption {...props} row={row} /> }}
              onChange={async (value) => {
                currentSetLoading(true)
                if (value)
                  await onChangeSelect(row, value.value, {
                    scoreCard: value.value,
                    judges: null,
                  })
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: '28%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem]">
          <span className="whitespace-nowrap">Judges</span>
        </span>
      ),
      cell: (row: RegistrationFeesTypeEx) =>
        row.loading ? (
          <div className="p-2 w-full">
            <Skeleton
              variant="rounded"
              width="100%"
              height={56}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          </div>
        ) : (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={false}
              isMulti={true}
              placeholder="Select judges"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  ...isFilled(row),
                }),
              }}
              value={
                row.judges && row.judges.length > 0 && judgesOptions && judgesOptions.length > 0
                  ? row.judges.map((judge) => getValues(judge ?? '', judgesOptions))
                  : []
              }
              options={judgesOptions ?? []}
              onChange={async (values) => {
                currentSetLoading(true)
                if (values) await onChangeJudge(row, values as IJudgeOption[])
                currentSetLoading(false)
              }}
            />
          </div>
        ),
      width: '28%',
    },
  ]

  const newRegistrationFees = registrationFees?.registrationFees?.flatMap((fee) => {
    const feeData = [
      {
        ...fee,
        value: fee.uuid || '',
        label: `${fee.name || ''}`,
        hasChildren: fee.children && Array.isArray(fee.children) && fee.children.length > 0,
      },
    ]

    if (fee.children && Array.isArray(fee.children) && fee.children.length > 0) {
      const childrenData = fee.children.map((child) => ({
        ...child,
        value: child.uuid || '',
        label: `${child.name || ''}`,
        parentId: fee.uuid || '',
      }))

      return childrenData as RegistrationFeesTypeEx[]
    }

    return feeData
  })

  return (
    <div className="mt-8">
      {currentLoading && (
        <div className="w-full bg-white/30 h-full fixed z-30 left-0 top-0 flex justify-center items-center">
          <ViewsLoader size="lg" color="#F70763" />
        </div>
      )}
      <InfiniteScrollDataTable
        hasMore={false}
        className="exhibitorListTable"
        columns={columnsJudging}
        data={[...(newRegistrationFees ?? [])]}
      />
    </div>
  )
}
